import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { BASE_URL } from "../../Utils/api";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { FaCopy } from "react-icons/fa";
import { ErroModal } from "../Generals";
import "./GodMode.css";


function formatarDataInput(valor) {
  if (!valor) return "";
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return String(valor).slice(0, 10);
  return d.toISOString().slice(0, 10);
}

function gerarCodigoCopia(origemCodigo) {
  const base = String(origemCodigo || "TP").replace(/[^A-Za-z0-9_]/g, "_").slice(0, 20);
  return `${base}_CP_${Date.now()}`.slice(0, 40);
}

function PlanosGodMode() {
  const token = localStorage.getItem("authToken");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [tabelas, setTabelas] = useState([]);
  const [idTabela, setIdTabela] = useState("");
  const [detalhe, setDetalhe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [copiando, setCopiando] = useState(false);
  const [showCopiarModal, setShowCopiarModal] = useState(false);
  const [formCopiar, setFormCopiar] = useState({
    nome: "",
    codigo: "",
    vigencia_inicio: "",
    vigencia_fim: "",
    ativo: "N",
  });
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const carregarTabelas = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErro("");
    try {
      const { data } = await api.get(`${BASE_URL}/api/godmode/planos/tabelas`, { headers });
      const lista = Array.isArray(data?.data) ? data.data : [];
      setTabelas(lista);
      if (!idTabela && lista.length) {
        const vigente = lista.find((t) => t.vigente === "S");
        setIdTabela(String((vigente || lista[0]).id));
      }
    } catch (err) {
      setErro(err.response?.data?.message || "Não foi possível listar tabelas de preços.");
    } finally {
      setLoading(false);
    }
  }, [headers, idTabela, token]);

  const carregarDetalhe = useCallback(async () => {
    if (!token || !idTabela) return;
    setLoading(true);
    setErro("");
    try {
      const { data } = await api.get(
        `${BASE_URL}/api/godmode/planos/tabelas/${idTabela}`,
        { headers }
      );
      setDetalhe(data?.data || null);
    } catch (err) {
      setDetalhe(null);
      setErro(err.response?.data?.message || "Não foi possível carregar a tabela selecionada.");
    } finally {
      setLoading(false);
    }
  }, [headers, idTabela, token]);

  useEffect(() => {
    carregarTabelas();
  }, [carregarTabelas]);

  useEffect(() => {
    if (idTabela) carregarDetalhe();
  }, [idTabela, carregarDetalhe]);

  const recursosLimites = useMemo(
    () => (detalhe?.recursos || []).filter((r) => r.grupo === "LIMITES"),
    [detalhe]
  );

  const recursosFlags = useMemo(
    () => (detalhe?.recursos || []).filter((r) => r.grupo === "RECURSOS"),
    [detalhe]
  );

  const obterLimite = (recursoCodigo, planoId) =>
    (detalhe?.limites || []).find(
      (l) => l.recurso_codigo === recursoCodigo && l.id_plano === planoId
    );

  const atualizarTabela = (campo, valor) => {
    setDetalhe((prev) =>
      prev ? { ...prev, tabela: { ...prev.tabela, [campo]: valor } } : prev
    );
  };

  const atualizarPlano = (planoId, campo, valor) => {
    setDetalhe((prev) =>
      prev
        ? {
            ...prev,
            planos: prev.planos.map((p) =>
              p.id === planoId ? { ...p, [campo]: valor } : p
            ),
          }
        : prev
    );
  };

  const atualizarLimite = (limiteId, patch) => {
    setDetalhe((prev) =>
      prev
        ? {
            ...prev,
            limites: prev.limites.map((l) =>
              l.id === limiteId ? { ...l, ...patch } : l
            ),
          }
        : prev
    );
  };

  const handleSalvar = async () => {
    if (!detalhe?.tabela?.id) return;
    setSalvando(true);
    setErro("");
    setSucesso("");
    try {
      const payload = {
        tabela: {
          nome: detalhe.tabela.nome,
          vigencia_inicio: formatarDataInput(detalhe.tabela.vigencia_inicio),
          vigencia_fim: detalhe.tabela.vigencia_fim
            ? formatarDataInput(detalhe.tabela.vigencia_fim)
            : null,
          ativo: detalhe.tabela.ativo,
        },
        planos: detalhe.planos.map((p) => ({
          id: p.id,
          nome: p.nome,
          descricao: p.descricao,
          texto_acao: p.texto_acao,
          destaque: p.destaque,
          ordem_exibicao: p.ordem_exibicao,
          preco_mensal: Number(p.preco_mensal),
          preco_anual: Number(p.preco_anual),
        })),
        limites: detalhe.limites.map((l) => ({
          id: l.id,
          valor_numerico: l.ilimitado ? null : l.valor_numerico,
          valor_booleano: l.valor_booleano,
          ilimitado: l.ilimitado,
        })),
      };

      const { data } = await api.put(
        `${BASE_URL}/api/godmode/planos/tabelas/${detalhe.tabela.id}`,
        payload,
        { headers }
      );
      setDetalhe(data?.data || detalhe);
      setSucesso(data?.message || "Configuração salva.");
      carregarTabelas();
    } catch (err) {
      setErro(err.response?.data?.message || "Erro ao salvar configuração de planos.");
    } finally {
      setSalvando(false);
    }
  };

  const abrirCopiarModal = () => {
    if (!detalhe?.tabela) return;
    const hoje = new Date().toISOString().slice(0, 10);
    setFormCopiar({
      nome: `Cópia de ${detalhe.tabela.nome || "tabela"}`,
      codigo: gerarCodigoCopia(detalhe.tabela.codigo),
      vigencia_inicio: hoje,
      vigencia_fim: "",
      ativo: "N",
    });
    setShowCopiarModal(true);
  };

  const atualizarFormCopiar = (campo, valor) => {
    setFormCopiar((prev) => ({ ...prev, [campo]: valor }));
  };

  const handleCopiar = async () => {
    if (!idTabela || !formCopiar.nome.trim()) return;
    setCopiando(true);
    setErro("");
    setSucesso("");
    try {
      const { data } = await api.post(
        `${BASE_URL}/api/godmode/planos/tabelas/${idTabela}/copiar`,
        {
          nome: formCopiar.nome.trim(),
          codigo: formCopiar.codigo.trim(),
          vigencia_inicio: formCopiar.vigencia_inicio,
          vigencia_fim: formCopiar.vigencia_fim || null,
          ativo: formCopiar.ativo,
        },
        { headers }
      );
      const novaTabela = data?.data;
      setShowCopiarModal(false);
      setDetalhe(novaTabela || null);
      if (novaTabela?.tabela?.id) {
        setIdTabela(String(novaTabela.tabela.id));
      }
      setSucesso(
        data?.message ||
          "Tabela copiada. Ajuste preços e limites, depois clique em Salvar alterações."
      );
      await carregarTabelas();
    } catch (err) {
      setErro(err.response?.data?.message || "Erro ao copiar tabela de preços.");
    } finally {
      setCopiando(false);
    }
  };

  if (loading && !detalhe) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" size="sm" /> Carregando planos…
      </div>
    );
  }

  return (
    <div className="godmode-planos">
      <ErroModal erro={erro} />
      {sucesso && <Alert variant="success">{sucesso}</Alert>}

      <Card className="godmode-clientes-card mb-3">
        <Card.Header className="d-flex flex-wrap align-items-center gap-2 justify-content-between">
          <strong>Tabela de preços</strong>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Form.Select
              size="sm"
              style={{ minWidth: 280 }}
              value={idTabela}
              onChange={(e) => setIdTabela(e.target.value)}
            >
              {tabelas.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome} ({t.codigo}){t.vigente === "S" ? " — vigente" : ""}
                </option>
              ))}
            </Form.Select>
            <Button variant="outline-secondary" size="sm" onClick={carregarDetalhe} disabled={loading}>
              Atualizar
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={abrirCopiarModal}
              disabled={!detalhe || copiando}
              title="Criar nova tabela a partir desta"
            >
              <FaCopy className="me-1" />
              Copiar
            </Button>
            <Button variant="primary" size="sm" onClick={handleSalvar} disabled={salvando || !detalhe}>
              {salvando ? "Salvando…" : "Salvar alterações"}
            </Button>
          </div>
        </Card.Header>

        {detalhe && (
          <Card.Body>
            <Row className="g-3 mb-4">
              <Col md={4}>
                <Form.Label>Nome</Form.Label>
                <Form.Control
                  size="sm"
                  value={detalhe.tabela.nome || ""}
                  onChange={(e) => atualizarTabela("nome", e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Form.Label>Vigência início</Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={formatarDataInput(detalhe.tabela.vigencia_inicio)}
                  onChange={(e) => atualizarTabela("vigencia_inicio", e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Form.Label>Vigência fim</Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={formatarDataInput(detalhe.tabela.vigencia_fim)}
                  onChange={(e) => atualizarTabela("vigencia_fim", e.target.value || null)}
                />
              </Col>
              <Col md={2}>
                <Form.Label>Ativo</Form.Label>
                <Form.Select
                  size="sm"
                  value={detalhe.tabela.ativo || "S"}
                  onChange={(e) => atualizarTabela("ativo", e.target.value)}
                >
                  <option value="S">Sim</option>
                  <option value="N">Não</option>
                </Form.Select>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                {detalhe.tabela.vigente === "S" ? (
                  <Badge bg="success">Vigente hoje</Badge>
                ) : (
                  <Badge bg="secondary">Fora da vigência</Badge>
                )}
              </Col>
            </Row>

            <h6 className="mb-2">Preços e planos</h6>
            <Table responsive bordered hover size="sm" className="mb-4">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Mensal (R$)</th>
                  <th>Anual (R$)</th>
                  <th>Destaque</th>
                  <th>Texto botão</th>
                </tr>
              </thead>
              <tbody>
                {detalhe.planos.map((plano) => (
                  <tr key={plano.id}>
                    <td><code>{plano.codigo}</code></td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={plano.nome || ""}
                        onChange={(e) => atualizarPlano(plano.id, "nome", e.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0"
                        step="0.01"
                        value={plano.preco_mensal}
                        onChange={(e) =>
                          atualizarPlano(plano.id, "preco_mensal", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0"
                        step="0.01"
                        value={plano.preco_anual}
                        onChange={(e) =>
                          atualizarPlano(plano.id, "preco_anual", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={plano.destaque || ""}
                        placeholder="Opcional"
                        onChange={(e) => atualizarPlano(plano.id, "destaque", e.target.value || null)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={plano.texto_acao || ""}
                        onChange={(e) => atualizarPlano(plano.id, "texto_acao", e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h6 className="mb-2">Limites por plano</h6>
            <div className="table-responsive mb-4">
              <Table bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Recurso</th>
                    {detalhe.planos.map((p) => (
                      <th key={p.id}>{p.codigo}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recursosLimites.map((recurso) => (
                    <tr key={recurso.id}>
                      <td>{recurso.nome_exibicao}</td>
                      {detalhe.planos.map((plano) => {
                        const limite = obterLimite(recurso.codigo, plano.id);
                        if (!limite) return <td key={plano.id}>—</td>;
                        if (recurso.tipo === "ILIMITADO") {
                          return (
                            <td key={plano.id} className="text-center">
                              Ilimitado
                            </td>
                          );
                        }
                        return (
                          <td key={plano.id}>
                            <Form.Check
                              type="checkbox"
                              className="mb-1"
                              label="Ilimitado"
                              checked={Boolean(limite.ilimitado)}
                              onChange={(e) =>
                                atualizarLimite(limite.id, {
                                  ilimitado: e.target.checked,
                                  valor_numerico: e.target.checked ? null : limite.valor_numerico,
                                })
                              }
                            />
                            {!limite.ilimitado && (
                              <Form.Control
                                size="sm"
                                type="number"
                                min="0"
                                value={limite.valor_numerico ?? ""}
                                onChange={(e) =>
                                  atualizarLimite(limite.id, {
                                    valor_numerico: e.target.value === "" ? null : Number(e.target.value),
                                  })
                                }
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <h6 className="mb-2">Recursos (flags)</h6>
            <div className="table-responsive">
              <Table bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Recurso</th>
                    {detalhe.planos.map((p) => (
                      <th key={p.id}>{p.codigo}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recursosFlags.map((recurso) => (
                    <tr key={recurso.id}>
                      <td>{recurso.nome_exibicao}</td>
                      {detalhe.planos.map((plano) => {
                        const limite = obterLimite(recurso.codigo, plano.id);
                        if (!limite) return <td key={plano.id}>—</td>;
                        return (
                          <td key={plano.id} className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={Boolean(limite.valor_booleano)}
                              onChange={(e) =>
                                atualizarLimite(limite.id, { valor_booleano: e.target.checked })
                              }
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <p className="text-muted small mt-3 mb-0">
              Alterações aqui afetam novas assinaturas e a página pública de preços. Entidades já
              ativas mantêm o snapshot de limites da assinatura vigente até renovação ou upgrade.
            </p>
          </Card.Body>
        )}
      </Card>

      <Modal show={showCopiarModal} onHide={() => setShowCopiarModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Copiar tabela de preços</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Será criada uma nova tabela com os mesmos preços e limites da selecionada.
            Depois você pode alterar os valores e salvar.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Nome da nova tabela</Form.Label>
            <Form.Control
              value={formCopiar.nome}
              onChange={(e) => atualizarFormCopiar("nome", e.target.value)}
              placeholder="Ex.: Tabela comercial jan/2026"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Código</Form.Label>
            <Form.Control
              value={formCopiar.codigo}
              onChange={(e) => atualizarFormCopiar("codigo", e.target.value.slice(0, 40))}
              placeholder="Identificador único (máx. 40 caracteres)"
            />
          </Form.Group>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Vigência início</Form.Label>
                <Form.Control
                  type="date"
                  value={formCopiar.vigencia_inicio}
                  onChange={(e) => atualizarFormCopiar("vigencia_inicio", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Vigência fim</Form.Label>
                <Form.Control
                  type="date"
                  value={formCopiar.vigencia_fim}
                  onChange={(e) => atualizarFormCopiar("vigencia_fim", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mt-3">
            <Form.Label>Ativo</Form.Label>
            <Form.Select
              value={formCopiar.ativo}
              onChange={(e) => atualizarFormCopiar("ativo", e.target.value)}
            >
              <option value="N">Não — rascunho (recomendado)</option>
              <option value="S">Sim</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCopiarModal(false)} disabled={copiando}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleCopiar}
            disabled={copiando || !formCopiar.nome.trim() || !formCopiar.vigencia_inicio}
          >
            {copiando ? "Copiando…" : "Copiar tabela"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default PlanosGodMode;
